import * as React from "react";
import {
  Lead,
  Customer,
  AppPermissions,
  useHasPermission,
  ListHeaderDropdown,
} from "../../core";
import api from "../../api";
import BreadCrumb from "./components/BreadCrumb";
import { RequirementStageStatus, TabNames } from "./components/props";
import Tabs from "./components/Tabs";
import SearchPropertyTab, {
  SearchPropertyTabRef,
} from "./components/SearchPropertyTab";
import RequirementDetails from "./components/RequirementDetails";
import {
  LeadRequirement,
  RequirementStatus,
} from "../../core/api/models/requirement";
import EditRequirement from "./components/EditRequirement";
import { toastr } from "react-redux-toastr";
import { Controller, useForm } from "react-hook-form";
import { DropdownList } from "react-widgets";
import ErrorMessage from "../../components/text/ErrorMessage";
import { useDataLoader } from "react-remote-data-hooks";
import VersionList from "./components/VersionList";
import "../../styles/full-view-popup.scss";
import "../../styles/sales-person.scss";
import "../../styles/tags.scss";
import "../../styles/requirement.scss";
import OwnerBlock from "../leads/components/assign-users/OwnerBlock";
import RequirementVersionsComparison from "./components/RequirementVersionsComparison";
import { Shortlist } from "../../core/api/models/project";
import CreateProposal from "../create-proposal";
import { useHistory, useParams } from "react-router-dom";
import { RouteKeys } from "../routes/route-keys";
import ContainerLoader from "../../components/loader/container-loader";

interface IConfigurationProps {
  leads?: Lead[];
  customer?: Customer;
  onClose?: () => void;
  reload?: () => void;
}

export interface FormProps {
  reason: string;
  comment?: string;
}

const Requirement: React.FunctionComponent<IConfigurationProps> = (props) => {
  const params = useParams<{ id: string }>();
  const requirementId = params.id;
  const history = useHistory();
  const searchPropertyRef = React.useRef<SearchPropertyTabRef>(null);

  const [canEdit, setCanEdit] = React.useState(false);
  const [syncData, setSyncData] = React.useState<boolean>(false);
  const [canCompareVersion, setCanCompareVersion] = React.useState(false);
  const [canVersionEdit, setCanVersionEdit] = React.useState(false);
  const [updating, setUpdating] = React.useState(false);
  const [verListId, setVerListId] = React.useState<string>("");
  const [rejectReason, setRejectReason] = React.useState<any>([]);

  const [modalOpen, setModalOpen] = React.useState<{
    isOpen?: boolean;
    status?: string;
  }>({});

  const [requirementModal, setRequirementModal] = React.useState<{
    isOpen?: boolean;
    requirementId?: string;
  }>({});

  const { control, errors, handleSubmit } = useForm<FormProps>({
    reValidateMode: "onChange",
    mode: "onSubmit",
  });

  const [tabName, setTabName] = React.useState<TabNames>(
    TabNames.RequirementDetails
  );

  const canRequirementStatusApprove = useHasPermission(
    AppPermissions.RequirementStatusApprove
  );

  const canRequirementStatusReject = useHasPermission(
    AppPermissions.RequirementStatusReject
  );

  const canCreateProposal = useHasPermission(AppPermissions.CreateProposal);

  const {
    data: configuration,
    loading,
    loaded,
    reload,
  } = useDataLoader<LeadRequirement>(() =>
    api.requirement.getRequirement(verListId || requirementId)
  );

  const { data: versionList, reload: reloadVersion } = useDataLoader<
    LeadRequirement[]
  >(
    async (masterRequirementId?: string) =>
      masterRequirementId
        ? api.requirement.getRequirementVersions(masterRequirementId)
        : [],
    [configuration?.masterRequirementId || configuration?._id]
  );

  const { data: sortList, reload: reloadShortList } = useDataLoader<Shortlist>(
    (masterRequirementId: string) =>
      masterRequirementId
        ? api.shortlist.getShortList(masterRequirementId)
        : undefined,
    [configuration?.masterRequirementId || configuration?._id]
  );

  const approvedVersions = versionList?.filter(
    (v) => v.status === RequirementStatus.Approved
  );

  React.useEffect(() => {
    const fetchRejectReason = async () => {
      const RejectReasonRes =
        await api.AddNewRequirement.loadAllRequirementRejectReason({
          entityType: "RequirementReject",
        });
      const enabledRejectReason = RejectReasonRes?.length
        ? RejectReasonRes.filter((item) => item?.visibility === true)
        : [];
      setRejectReason(enabledRejectReason);
    };

    fetchRejectReason();
  }, []);

  React.useEffect(() => {
    if (configuration?.masterRequirementId || configuration?._id) {
      reloadVersion(configuration?.masterRequirementId || configuration?._id);
      reloadShortList(configuration?.masterRequirementId || configuration?._id);
    }
  }, [configuration]);

  const handleTabChange = (tabNameValue: TabNames) => {
    if (tabNameValue === TabNames.ConnectedProposal) {
      history.push(`${RouteKeys.Proposal.replace(":id", requirementId)}`);
    } else {
      setTabName(tabNameValue);
    }
  };

  const handleApproveStatus = async () => {
    setUpdating(true);
    try {
      const approvalRes = await api.AddNewRequirement.approveStatus({
        comment: "",
        id: verListId ? verListId : configuration?._id,
        action: "approved",
      });

      if (approvalRes) {
        if (
          verListId ||
          configuration?.masterRequirementId ||
          configuration?._id
        ) {
          reloadVersion(
            configuration?.masterRequirementId || configuration?._id
          );
        }
        if (verListId) {
          history.replace(`${RouteKeys.Requirement.replace(":id", verListId)}`);
          reload();
        } else {
          reload();
        }
        // reload()
        setVerListId("");
        toastr.success("Success", "Requirements Status is Approved");
      }
      setModalOpen({});
      props.reload?.();
    } catch (error) {
      toastr.error("Error", "Failed to Approve Status");
    }
    setUpdating(false);
  };
  const handleRejectStatus = async (data: any) => {
    setUpdating(true);
    try {
      const rejectRes = await api.AddNewRequirement.rejectStatus({
        comment: data?.comment ?? "",
        reason: data?.reason ?? "",
        id: verListId ? verListId : configuration?._id,
        action: "rejected",
      });

      if (rejectRes) {
        if (verListId) {
          reloadVersion(
            configuration?.masterRequirementId || configuration?._id
          );
        }
        toastr.success("Success", "Requirements Status is Rejected");
        setVerListId("");
        props.onClose?.();
      }
      setModalOpen({});
      reload();
      props.reload?.();
    } catch (error) {
      toastr.error("Error", "Failed to Reject Status");
    }
    setUpdating(false);
  };
  const handleRevokeStatus = async () => {
    setUpdating(true);
    try {
      const revokeRes = await api.AddNewRequirement.revokeStatus({
        comment: "",
        id: configuration?._id,
        action: "revoke",
      });
      if (revokeRes) {
        toastr.success("Success", "Requirements Status is Revoked");
      }
      setModalOpen({});
      reload();
      props.reload?.();
    } catch (error) {
      toastr.error("Error", "Failed to Revoke Status");
    }
    setUpdating(false);
  };

  const renderRejectModel = () => {
    return modalOpen?.isOpen && modalOpen.status === "Reject" ? (
      <>
        <div
          className="modal fade show in mark-as-lost"
          id="exampleModal"
          aria-labelledby="exampleModalLabel"
          aria-hidden="true">
          <div className="modal-dialog modal-dialog-confirm modal-dialog-centered">
            <div className="modal-content rounded-0">
              <div className="modal-body text-center">
                <form onSubmit={handleSubmit(handleRejectStatus)}>
                  <div className="container">
                    <div className="row">
                      <div className="col">
                        <p>Are you sure want to reject this requirement</p>
                      </div>
                    </div>

                    <div className="row">
                      <div className="col">
                        <div className="form-group font-weight-semibold mb-3 text-left">
                          <label htmlFor="" className="font-sm text-uppercase">
                            Reason<span className="text-danger">*</span>
                          </label>
                          <Controller
                            control={control}
                            name="reason"
                            rules={{ required: true }}
                            render={({ onChange, value, name }) => (
                              <DropdownList
                                value={value}
                                data={rejectReason}
                                defaultValue={value}
                                textField={"reason"}
                                valueField={"reason"}
                                placeholder={"Select Reason"}
                                onChange={(val) => {
                                  onChange(val.reason);
                                }}
                              />
                            )}
                          />
                          {errors.reason && (
                            <ErrorMessage>Reason is required</ErrorMessage>
                          )}
                        </div>

                        <div className="form-group font-weight-semibold mb-3 text-left">
                          <label htmlFor="" className="font-sm text-uppercase">
                            Comments
                          </label>
                          <Controller
                            control={control}
                            name="comment"
                            render={({ onChange, value, name }) => (
                              <input
                                type="text"
                                className="form-control rounded-sm font-weight-semibold"
                                placeholder="Enter comment"
                                name={name}
                                value={value}
                                onChange={onChange}
                                id="comment"
                              />
                            )}
                          />
                        </div>
                      </div>
                    </div>
                    <div className="row">
                      <div className="col-auto mr-auto">
                        <button
                          type="button"
                          className="btn btn-outline-dark py-2 font-weight-semibold font-normal px-4 mr-1"
                          onClick={() => setModalOpen({})}>
                          Cancel
                        </button>
                      </div>
                      <div className="col-auto">
                        <button
                          type="submit"
                          className="btn btn-primary py-2 font-weight-semibold font-normal px-4 "
                          disabled={updating}>
                          Reject
                        </button>
                      </div>
                    </div>
                  </div>
                </form>
              </div>
            </div>
          </div>
        </div>
        <div className="modal-backdrop fade show"></div>
      </>
    ) : (
      <></>
    );
  };
  const handleSync = async () => {
    try {
      setSyncData(true);
      const response = await api.project.syncEcom();
      if (response.status === 200) {
        if (configuration?.masterRequirementId || configuration?._id) {
          await searchPropertyRef?.current?.refreshProperty();
          toastr.success(
            "Properties Sync Done",
            `Projects: ${response?.data?.totalProjectsCount} , Properties: ${response?.data?.totalPropertiesCount}`
          );
        }
      } else {
        toastr.error("Error", "Failed to Sync ");
      }
    } catch (error) {
      toastr.error("Error", "Something went wrong");
    } finally {
      setSyncData(false);
    }
  };

  const renderConfirm = () => {
    return modalOpen?.isOpen && modalOpen.status !== "Reject" ? (
      <>
        <div
          className="modal fade show in"
          id="exampleModal"
          aria-labelledby="exampleModalLabel"
          aria-hidden="true">
          <div className="modal-dialog modal-dialog-confirm modal-dialog-centered">
            <div className="modal-content rounded-0">
              <div className="modal-body text-center">
                <div className="container">
                  <div className="row">
                    <div className="col">
                      <p>
                        Are you sure want to {modalOpen.status} this requirement
                      </p>
                    </div>
                  </div>
                  <div className="row">
                    <div className="col-auto mr-auto">
                      <button
                        type="button"
                        className="btn btn-outline-dark py-2 font-weight-semibold font-normal px-4 mr-1"
                        onClick={() => setModalOpen({})}>
                        Cancel
                      </button>
                    </div>
                    <div className="col-auto">
                      <button
                        type="submit"
                        className="btn btn-primary py-2 font-weight-semibold font-normal px-4 "
                        onClick={() => {
                          if (modalOpen.status === "Approve") {
                            handleApproveStatus();
                          }

                          if (modalOpen.status === "Revoke") {
                            handleRevokeStatus();
                          }
                        }}
                        disabled={updating}>
                        {modalOpen.status}
                      </button>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
        <div className="modal-backdrop fade show"></div>
      </>
    ) : (
      <></>
    );
  };

  const actions: any[] = [];
  if (!!sortList?.shortListedProperties?.length && canCreateProposal && !configuration?.hasAnyProposalApproved) {
    actions.push({
      text: "Create Proposal",
      iconClass: "bg-blue",
      onClick: () =>
        setRequirementModal({
          isOpen: true,
          requirementId:
            configuration?.masterRequirementId || configuration?._id,
        }),
    });
  }

  const renderHeader = () => (
    <div className="container-fluid bg-gray-100 border-bottom py-3">
      <div className="row align-items-center">
        {canCompareVersion ? (
          <>
            <div
              className="col-auto"
              onClick={() => {
                props.onClose?.();
                setCanCompareVersion(!canCompareVersion);
              }}>
              <i className="icon-arrow-left text-gray-500 font-xxl cursor-pointer"></i>
            </div>

            <label className="font-weight-semibold">Compare Requirement</label>
          </>
        ) : (
          <>
            <div
              className="col-auto"
              onClick={() => {
                history.goBack();
                props.onClose?.();
              }}>
              <i className="icon-arrow-left text-gray-500 font-xxl cursor-pointer"></i>
            </div>
            <div className="co">
              {configuration && (
                <BreadCrumb
                  configuration={configuration}
                  requirementId={requirementId}
                />
              )}
            </div>
            <div className="mt-md-0 ml-auto col-auto">
              {versionList?.length === 0 &&
                configuration?.status === RequirementStatus.Pending && (
                  <span className="bg-white d-inline-block mr-2">
                    <button
                      onClick={() => setCanEdit(true)}
                      className="btn btn-outline-secondary px-4 font-normal font-weight-bold py-2 rounded">
                      Edit
                    </button>
                  </span>
                )}
              {/* {configuration?.status === RequirementStatus.Approved && canRequirementStatusRevoke  && (
            <span className="bg-white d-inline-block mr-2">
              <button
                onClick={() =>
                  setModalOpen({
                    isOpen: true,
                    status: "Revoke",
                  })
                }
                 disabled={updating}
                className="btn btn-outline-secondary px-3 font-normal font-weight-bold py-2 rounded"
              >
                Revoke
              </button>
            </span>
          )} */}
              {configuration?.status === RequirementStatus.Pending &&
                !canEdit &&
                !canVersionEdit &&
                canRequirementStatusReject && (
                  <span className="bg-white d-inline-block mr-2">
                    <button
                      onClick={() =>
                        setModalOpen({
                          isOpen: true,
                          status: "Reject",
                        })
                      }
                      disabled={updating}
                      className="btn btn-outline-secondary px-3 font-normal font-weight-bold py-2 rounded">
                      Reject
                    </button>
                  </span>
                )}
              {configuration?.status === RequirementStatus.Pending &&
                !canEdit &&
                !canVersionEdit &&
                canRequirementStatusApprove && (
                  <button
                    onClick={() => {
                      setModalOpen({
                        isOpen: true,
                        status: "Approve",
                      });
                    }}
                    disabled={updating}
                    className="btn btn-primary px-3 mr-2 font-normal font-weight-bold py-2 rounded">
                    Approve
                  </button>
                )}
            </div>
            {!configuration?.hasAnyProposalApproved && tabName === TabNames.Properties && (
              <button
                className="btn btn-outline-primary px-4 mx-4 font-normal font-weight-bold py-2 rounded"
                onClick={handleSync}
                disabled={syncData}>
                Sync
              </button>
            )}

            {!!actions?.length && (
              <div>
                <ListHeaderDropdown text="Action" actions={actions} />
              </div>
            )}

            <div className="col-auto mt-md-0">
              <OwnerBlock
                user={configuration?.salesPerson}
                title="Transaction Manager"
                // title="Support Executive"
                className={`d-inline-flex align-items-center`}></OwnerBlock>
            </div>
          </>
        )}
      </div>
    </div>
  );

  const getConfigurationByVersionId = (verListArr: any[], id: string) => {
    return verListArr?.find((item) => item?._id === id);
  };

  return !requirementModal?.isOpen ? (
    <>
      <div
        className={`bg-gray-100 detail-poupup  lead-detail position-fixed top-0 w-100 h-100 overflow-auto in`}
        style={{ zIndex: 1051 }}>
        {renderHeader()}
        <div className="container-fluid px-4 bg-white min-vh-100 pt-3 pb-5 fff">
          {!loaded || loading ? (
            <ContainerLoader height={200}></ContainerLoader>
          ) : (
            <>
              {canCompareVersion ? (
                <RequirementVersionsComparison parentId={verListId} />
              ) : (
                <>
                  <div className="">
                    <Tabs
                      tabName={tabName}
                      tabChangeHandler={handleTabChange}
                      status={configuration?.status!}
                      showVersion={!!versionList?.length}
                      showPropertyTab={!!approvedVersions?.length}
                    />
                  </div>
                  {tabName === TabNames.RequirementDetails && (
                    <>
                      {canEdit ? (
                        <EditRequirement
                          requirement={configuration}
                          onClose={() => {
                            setCanEdit(false);
                            setCanVersionEdit(false);
                            setVerListId("");
                          }}
                          onReload={(id: string) => {
                            setCanEdit(false);
                            setCanVersionEdit(false);
                            setVerListId("");
                            reload({ requirementId: id });
                            if (
                              configuration?.masterRequirementId ||
                              configuration?._id
                            ) {
                              reloadVersion(
                                configuration?.masterRequirementId ||
                                  configuration?._id
                              );
                            }
                          }}></EditRequirement>
                      ) : (
                        <RequirementDetails
                          requirement={configuration}
                          onEdit={() => setCanEdit(true)}
                          versions={versionList}></RequirementDetails>
                      )}
                    </>
                  )}
                  {tabName === TabNames.Properties && (
                    <>
                      <SearchPropertyTab
                        ref={searchPropertyRef}
                        requirementId={approvedVersions?.[0]?._id}
                        reload={() =>
                          reloadVersion(
                            configuration?.masterRequirementId ||
                              configuration?._id
                          )
                        }
                        sortList={sortList}
                        reloadShortList={() =>
                          reloadShortList(
                            configuration?.masterRequirementId ||
                              configuration?._id
                          )
                        }
                        configuration={configuration}
                        syncData={syncData}
                      />
                    </>
                  )}
                  {tabName === TabNames.Versions && (
                    <>
                      {canVersionEdit ? (
                        <EditRequirement
                          requirement={getConfigurationByVersionId(
                            versionList,
                            verListId
                          )}
                          onClose={() => {
                            setCanVersionEdit(false);
                            setVerListId("");
                          }}
                          onReload={(id: string) => {
                            setCanVersionEdit(false);
                            setVerListId("");
                            reloadVersion(
                              configuration?.masterRequirementId ||
                                configuration?._id
                            );
                          }}></EditRequirement>
                      ) : (
                        <VersionList
                          canRequirementStatusApprove={canRequirementStatusApprove}
                          canRequirementStatusReject={canRequirementStatusReject}
                          versions={versionList}
                          onButtonAction={(id: string, action: string) => {
                            setVerListId(id);
                            setModalOpen({
                              isOpen: true,
                              status: action,
                            });
                          }}
                          onCompare={(id: string) => {
                            setVerListId(id);
                            setCanCompareVersion(!canCompareVersion);
                          }}
                          onVersionEdit={(id: string) => {
                            setCanVersionEdit(!canVersionEdit);
                            setVerListId(id);
                          }}
                        />
                      )}
                    </>
                  )}
                </>
              )}
            </>
          )}
        </div>
        {renderRejectModel()}
        {renderConfirm()}
      </div>
      <div className="modal-backdrop fade show" style={{ zIndex: 1050 }}></div>
    </>
  ) : (
    <>
      <CreateProposal
        requirementId={requirementModal.requirementId!}
        onClose={() => setRequirementModal({})}
        isRedirect
        reloadRequirement={() =>
          reloadVersion(
            configuration?.masterRequirementId || configuration?._id
          )
        }></CreateProposal>
    </>
  );
};

export default Requirement;
